import {
  Box,
  Button,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  Radio,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { useBoundStore } from "useStore";
import LightTooltip from "views/Components/Shared/Components/LightTooltip";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StaticDateRangePicker } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import { timeRangeOptions } from "./utils";

const RADIO_STYLES = {
  color: "#354887",
  "&.Mui-checked": {
    color: "#354887",
  },
};

const DETECTION_DELAY = 100;
const AUTO_DETECT_RESET_DELAY = 50;

// Funciones helper
const convertStoreDateToDayjs = (storeDate) => {
  if (!storeDate) return null;
  if (Array.isArray(storeDate)) {
    return storeDate.map((d) => (d ? dayjs(d) : null));
  }
  return storeDate;
};

const getDateRangeForTimeRange = (timeRange) => {
  const today = dayjs();
  const ranges = {
    today: [today, today],
    yesterday: [today.subtract(1, "day"), today.subtract(1, "day")],
    todayAndYesterday: [today.subtract(1, "day"), today],
    last7Days: [today.subtract(7, "day"), today],
    last14Days: [today.subtract(14, "day"), today],
    last30Days: [today.subtract(30, "day"), today],
    last45Days: [today.subtract(45, "day"), today],
    lastWeek: [today.startOf("week"), today.endOf("week")],
    lastMonth: [today.startOf("month"), today.endOf("month")],
    custom: null,
  };
  return ranges[timeRange] ?? null;
};

const detectTimeRangeType = (dateRange) => {
  if (!dateRange?.[0] || !dateRange?.[1]) return "custom";

  const today = dayjs().startOf("day");
  const yesterday = today.subtract(1, "day");
  const startDate = dayjs(dateRange[0]).startOf("day");
  const endDate = dayjs(dateRange[1]).startOf("day");

  // Patrones de detección
  const patterns = [
    {
      type: "today",
      check: () =>
        startDate.isSame(today, "day") && endDate.isSame(startDate, "day"),
    },
    {
      type: "yesterday",
      check: () =>
        startDate.isSame(yesterday, "day") && endDate.isSame(startDate, "day"),
    },
    {
      type: "todayAndYesterday",
      check: () =>
        startDate.isSame(yesterday, "day") && endDate.isSame(today, "day"),
    },
    {
      type: "last7Days",
      check: () =>
        startDate.isSame(today.subtract(7, "day"), "day") &&
        endDate.isSame(today, "day"),
    },
    {
      type: "last14Days",
      check: () =>
        startDate.isSame(today.subtract(14, "day"), "day") &&
        endDate.isSame(today, "day"),
    },
    {
      type: "last30Days",
      check: () =>
        startDate.isSame(today.subtract(30, "day"), "day") &&
        endDate.isSame(today, "day"),
    },
    {
      type: "last45Days",
      check: () =>
        startDate.isSame(today.subtract(45, "day"), "day") &&
        endDate.isSame(today, "day"),
    },
    {
      type: "lastWeek",
      check: () =>
        startDate.isSame(today.startOf("week"), "day") &&
        endDate.isSame(today.endOf("week"), "day"),
    },
    {
      type: "lastMonth",
      check: () =>
        startDate.isSame(today.startOf("month"), "day") &&
        endDate.isSame(today.endOf("month"), "day"),
    },
  ];

  const matched = patterns.find((pattern) => pattern.check());
  return matched?.type || "custom";
};

const isValidDateRange = (dateRange) => {
  return (
    dateRange &&
    Array.isArray(dateRange) &&
    dateRange.length === 2 &&
    dateRange[0] &&
    dateRange[1]
  );
};

// Componente de Radio Button
const TimeRangeRadio = ({ value, label, checked, onChange }) => (
  <FormControlLabel
    control={<Radio checked={checked} sx={RADIO_STYLES} />}
    label={label}
    onClick={() => onChange(value)}
  />
);

// Componente principal de opciones
function Options({
  closeTooltip,
  isOpen,
  date,
  setDate,
  selectedTimeRange,
  setSelectedTimeRange,
}) {
  // Refs
  const isInitializedRef = useRef(false);
  const isAutoDetectingRef = useRef(false);

  const [sectionShown, setSectionShown] = useState(
    selectedTimeRange || "last7Days",
  );
  const [dateRangeEdit, setDateRangeEdit] = useState([dayjs(), null]);

  // Inicializar desde el store cuando se abre el tooltip
  useEffect(() => {
    if (isOpen && !isInitializedRef.current) {
      setSectionShown(selectedTimeRange || "last7Days");

      const converted = date ? convertStoreDateToDayjs(date) : null;
      setDateRangeEdit(
        converted && Array.isArray(converted) && converted.length === 2
          ? converted
          : [dayjs(), null],
      );

      isInitializedRef.current = true;
    } else if (!isOpen) {
      isInitializedRef.current = false;
    }
  }, [isOpen, date, selectedTimeRange]);

  // Detectar tipo de rango cuando se selecciona manualmente en el calendario
  useEffect(() => {
    if (!isValidDateRange(dateRangeEdit)) return;

    const detectedType = detectTimeRangeType(dateRangeEdit);
    if (detectedType === sectionShown) return;

    const timeoutId = setTimeout(() => {
      isAutoDetectingRef.current = true;
      setSectionShown(detectedType);
      setTimeout(() => {
        isAutoDetectingRef.current = false;
      }, AUTO_DETECT_RESET_DELAY);
    }, DETECTION_DELAY);

    return () => clearTimeout(timeoutId);
  }, [dateRangeEdit, sectionShown]);

  // Actualizar dateRangeEdit cuando se selecciona una opción rápida
  useEffect(() => {
    if (isAutoDetectingRef.current) return;

    const newRange = getDateRangeForTimeRange(sectionShown);
    if (newRange !== null) {
      setDateRangeEdit(newRange);
    }
  }, [sectionShown]);

  const handleSubmit = useCallback(() => {
    setDate(dateRangeEdit);
    setSelectedTimeRange(sectionShown);
    closeTooltip();
  }, [
    dateRangeEdit,
    sectionShown,
    setDate,
    setSelectedTimeRange,
    closeTooltip,
  ]);

  const handleTimeRangeChange = useCallback((value) => {
    setSectionShown(value);
  }, []);

  const formattedDateRange = useMemo(() => {
    const start = dateRangeEdit[0]?.format("DD/MM/YYYY") || "";
    const end = dateRangeEdit[1]?.format("DD/MM/YYYY") || "";
    return end ? `${start} - ${end}` : start;
  }, [dateRangeEdit]);

  return (
    <List
      sx={{
        width: "auto",
        minWidth: "850px",
        maxWidth: "1200px",
        bgcolor: "background.paper",
        paddingX: "10px",
        paddingY: "10px",
        overflow: "hidden",
      }}
      component="nav"
      aria-labelledby="nested-list-subheader"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        width="100%"
      >
        <FormGroup
          sx={{
            display: "flex",
            alignItems: "start",
            flexDirection: "column",
            justifyContent: "center",
            paddingX: "10px",
            width: "300px",
          }}
        >
          {timeRangeOptions.map((option) => (
            <TimeRangeRadio
              key={option.value}
              value={option.value}
              label={option.label}
              checked={sectionShown === option.value}
              onChange={handleTimeRangeChange}
            />
          ))}
        </FormGroup>

        <Divider orientation="vertical" flexItem />

        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ paddingRight: "10px", flex: 1 }}
        >
          <Typography
            sx={{
              color: "rgba(0, 0, 0, 0.87)",
              fontFamily: "Roboto",
              fontSize: "16px",
              lineHeight: "24px",
              fontWeight: 700,
              letterSpacing: "0.15px",
              paddingY: 1,
              textAlign: "center",
            }}
          >
            {timeRangeOptions.find((option) => option.value === sectionShown)
              ?.label ||
              timeRangeOptions.find(
                (option) => option.value === selectedTimeRange,
              )?.label}
            : {formattedDateRange}
          </Typography>

          <Divider orientation="horizontal" flexItem sx={{ mx: 2, my: 1 }} />

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                maxWidth: "700px",
                "& .MuiDateRangeCalendar-root": { width: "100%" },
                "& .MuiPickersCalendarHeader-root": { paddingX: 1 },
                "& .MuiPickersLayout-root": { width: "100%" },
              }}
            >
              <StaticDateRangePicker
                value={dateRangeEdit}
                onChange={setDateRangeEdit}
                displayStaticWrapperAs="desktop"
                calendars={2}
                // TODO: Limitar el rango de fechas a 1 año hacia atrás
                minDate={dayjs().subtract(1, "year")}
                // TODO: Limitar el rango de fechas días hacia adelante a hoy
                maxDate={dayjs()}
                sx={{ width: "100%" }}
                renderInput={(startProps, endProps) => (
                  <>
                    <TextField {...startProps} size="small" />
                    <Box sx={{ mx: 1 }}> - </Box>
                    <TextField {...endProps} size="small" />
                  </>
                )}
              />
            </Box>
          </LocalizationProvider>

          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            sx={{ width: "100%", mt: 2 }}
          >
            <Stack direction="row" spacing={1}>
              <Button
                color="secondary"
                onClick={() => {
                  closeTooltip();
                  setSelectedTimeRange("last7Days");
                }}
                sx={{ color: "#3F51B5" }}
              >
                CANCELAR
              </Button>
              <Button
                color="secondary"
                onClick={handleSubmit}
                sx={{ color: "#3F51B5" }}
              >
                OK
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </List>
  );
}

export default function TimeRangeGraph({
  date,
  setDate,
  selectedTimeRange,
  setSelectedTimeRange,
}) {
  const [open, setOpen] = useState(false);

  const handleTooltipClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleToggleTooltip = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <div>
        <LightTooltip
          PopperProps={{ disablePortal: true }}
          onClose={handleTooltipClose}
          open={open}
          placement="bottom-end"
          customStyles={{ maxWidth: "none" }}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          title={
            <Options
              closeTooltip={handleTooltipClose}
              isOpen={open}
              date={date}
              setDate={setDate}
              selectedTimeRange={selectedTimeRange}
              setSelectedTimeRange={setSelectedTimeRange}
            />
          }
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
            sx={{
              padding: "12px",
              width: "fit-content",
              borderRadius: "4px",
              border: "1px solid rgba(0, 0, 0, 0.23)",
              cursor: "pointer",
            }}
            onClick={handleToggleTooltip}
          >
            <Typography
              sx={{
                color: "#00000099",
                fontSize: "16px",
                fontFamily: "Roboto",
                fontWeight: 400,
                lineHeight: "24px",
                letterSpacing: "0.15px",
              }}
            >
              {/* 01/02/2026 - 25/02/2026 format */}
              {date?.[0]?.format("DD/MM/YYYY")}{" "}
              {date?.[0] && date?.[1] && " - "}{" "}
              {date?.[1]?.format("DD/MM/YYYY")}
            </Typography>
            <CalendarTodayOutlinedIcon
              sx={{
                color: "#0000008F",
                width: "24px",
                height: "24px",
              }}
            />
          </Stack>
        </LightTooltip>
      </div>
    </ClickAwayListener>
  );
}
